import { combineReducers } from 'redux';
import { createMigrate, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { SET_NOTIFICATION_MSG, setTokenAction, USER_LOGOUT, userLogoutAction } from '../actions';
import { loginReducerMigration } from '../migration';
import advertising, { advertisingSelector } from './advertising.reducer';
import advertisingSettings, { advertisingSettingsSelector } from './advertisingSettings.reducer';
import agency, { agencySelector } from './agency.reducer';
import alerts, { alertsSelector } from './alerts.reducer';
import amazonTraffic, { amazonTrafficSelector } from './amazonTraffic.reducer';
import amazonTrafficSettings from './amazonTrafficSettings.reducer';
import app from './app.reducer';
import billing from './billing.reducer';
import dashboard, { dashboardSelector } from './dashboard.reducer';
import dataTable, { dataTableSelector } from './dataTable.reducer';
import emailTool, { emailToolStateSelector } from './emailTool.reducer';
import expenses, { expensesSelector } from './expenses.reducer';
import globalSystemNotification, {
  globalSystemNotificationSelector
} from './globalSystemNotification.reducer';
import inventory, { inventorySelector } from './inventory.reducer';
import login, { loginSelector } from './login.reducer';
import monthlyBrandReview, { monthlyBrandReviewSelector } from './monthlyBrandReview.reducer';
import network, { networkSelector } from './network.reducer';
import notes from './notes.reducer';
import notification, { notificationSelector } from './notification.reducer';
import orders, { ordersSelector } from './orders.reducer';
import pnlDashboard, { pnlDashboardSelector } from './pnlDashboard.reducer';
import products, { productsSelector } from './products.reducer';
import productSearch, { productSearchSelector } from './productSearch.reducer';
import reactBoundary, { reactBoundarySelector } from './reactBoundary.reducer';
import refunds from './refunds.reducer';
import reports, { reportsSelector } from './reports.reducer';
import selector, { selectorSelector } from './selector.reducer';
import sentry, { sentrySelector } from './sentry.reducer';
import ui, { uiSelector } from './ui.reducer';
import user, { userSelector } from './user.reducer';
import userNotifications, { userNotificationsSelector } from './userNotifications.reducer';
import userPreferences, { userPreferencesSelector } from './userPreferences.reducer';

export const LOGIN_REDUCER_KEY = 'login';
export const REHYDRATE_ACTION_TYPE = 'persist/REHYDRATE';

const createReducer = asyncReducers =>
  combineReducers({
    ui,
    notification,
    globalSystemNotification,
    reactBoundary,
    userPreferences,
    dataTable,
    emailTool,
    agency,
    app,
    billing,
    dashboard,
    login: persistReducer(
      {
        key: LOGIN_REDUCER_KEY,
        storage,
        blacklist: ['token'],
        version: 1,
        migrate: createMigrate(loginReducerMigration)
      },
      login
    ),
    advertisingSettings,
    amazonTrafficSettings,
    network,
    notes,
    orders,
    products,
    productSearch,
    refunds,
    selector,
    user,
    inventory,
    reports,
    expenses,
    amazonTraffic,
    alerts,
    monthlyBrandReview,
    pnlDashboard,
    advertising,
    sentry,
    userNotifications,
    ...asyncReducers
  });

const nonPersistReducers = {
  ui,
  notification,
  dataTable,
  emailTool,
  agency,
  app,
  billing,
  dashboard,
  network,
  notes,
  orders,
  products,
  productSearch,
  refunds,
  selector,
  user,
  inventory,
  reports,
  expenses,
  amazonTraffic,
  alerts,
  monthlyBrandReview,
  pnlDashboard,
  advertising,
  sentry,
  userNotifications
};

const getInitialState = () =>
  Object.keys(nonPersistReducers).reduce(
    (acc, key) => ({ ...acc, [key]: nonPersistReducers[key](undefined, {}) }),
    {}
  );

const persistReducersLogoutActions = [() => setTokenAction(null), () => userLogoutAction()]; // FIXME: move token from local-storage to redux-persist

const appReducer = createReducer();

const rootReducer = (state, action) => {
  if (action.type === SET_NOTIFICATION_MSG && state.network.serverUnavailable && action.msg) {
    return state;
  }
  if (action.type === USER_LOGOUT) {
    const initialState = getInitialState();
    initialState.ui = {
      ...initialState.ui,
      fontReady: true,
      viewportHeight: state.ui.viewportHeight
    };
    const persistReducersInitialState = persistReducersLogoutActions.reduce(
      (accState, logoutAction) => appReducer(accState, logoutAction()),
      state
    );
    return { ...persistReducersInitialState, ...initialState };
  }
  return appReducer(state, action);
};

export {
  advertisingSelector,
  advertisingSettingsSelector,
  agencySelector,
  alertsSelector,
  amazonTrafficSelector,
  dashboardSelector,
  dataTableSelector,
  emailToolStateSelector,
  expensesSelector,
  globalSystemNotificationSelector,
  inventorySelector,
  loginSelector,
  monthlyBrandReviewSelector,
  networkSelector,
  notificationSelector,
  ordersSelector,
  pnlDashboardSelector,
  productSearchSelector,
  productsSelector,
  reactBoundarySelector,
  reportsSelector,
  selectorSelector,
  sentrySelector,
  uiSelector,
  userNotificationsSelector,
  userPreferencesSelector,
  userSelector
};

export default rootReducer;
